import { defineStore } from "pinia";
import * as signalR from "@microsoft/signalr";
import { getAccessToken } from "@/services/auth";

const baseUrl = import.meta.env.VITE_REALTIME_SERVICE_URL;
const endpoint = `${baseUrl}/views`;

export enum ReceivedRealTimeServiceEventType {
  PRESENCE_UPDATED = "PresenceUpdated",
}

export enum ViewsHubEvent {
  PRESENCE_UPDATED = "PresenceUpdated",
}

export enum ViewsHubFunction {
  ENTER_VIEW = "enterView",
  LEAVE_VIEW = "leaveView",
}

export type EventSubscription = {
  subscriber: Symbol;
  listener: Function;
};

export const useRealtimeStore = defineStore("realtime", () => {
  let connection: signalR.HubConnection | null = null;

  const isConnected = ref(false);

  const RECONNECT_BASE_INTERVAL = 5000;
  const RECONNECT_MAX_INTERVAL = 60000;
  let reconnectAttempts = 0;
  let reconnectTimer: number | null = null;
  let isIntentionalDisconnect = false;

  const subscriptions: Record<ReceivedRealTimeServiceEventType, EventSubscription[]> = {
    [ReceivedRealTimeServiceEventType.PRESENCE_UPDATED]: [],
  };

  const getSubscribers = (eventType: ReceivedRealTimeServiceEventType) =>
    subscriptions[eventType] ?? [];

  const subscribe = (eventType: ReceivedRealTimeServiceEventType, eventSubscription: EventSubscription) => {
    const list = subscriptions[eventType];
    const index = list.findIndex((e) => e.subscriber === eventSubscription.subscriber);
    if (index === -1) list.push(eventSubscription);
    else list[index] = eventSubscription;
  };

  const unsubscribe = (eventType: ReceivedRealTimeServiceEventType, subscriber: Symbol) => {
    const list = subscriptions[eventType];
    const index = list.findIndex((e) => e.subscriber === subscriber);
    if (index !== -1) list.splice(index, 1);
  };

  const scheduleReconnect = async (): Promise<void> => {
    if (isIntentionalDisconnect || reconnectTimer || isConnected.value) {
      return;
    }

    const interval = Math.min(RECONNECT_BASE_INTERVAL * 2 ** reconnectAttempts, RECONNECT_MAX_INTERVAL);
    reconnectAttempts++;

    return new Promise((resolve) => {
      reconnectTimer = window.setTimeout(async () => {
        reconnectTimer = null;

        try {
          await connect();
        } catch (err) {
          console.warn("realtime.store -> reconnect failed, retrying…", err);
          await scheduleReconnect();
        } finally {
          resolve();
        }
      }, interval);
    });
  };

  const createConnection = async () => {
    const accessToken = await getAccessToken();

    if (!accessToken)
      throw Error("no access token provided. unable to connect to realtime service.");

    const hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(endpoint, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets,
        accessTokenFactory: () => accessToken,
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    hubConnection.onclose((error) => {
      if (isIntentionalDisconnect) return;

      isConnected.value = false;
      connection = null;

      console.warn("realtime.store -> connection closed", error);
      scheduleReconnect();
    });

    hubConnection.on(ViewsHubEvent.PRESENCE_UPDATED, (payload) => {
      const subs = getSubscribers(ReceivedRealTimeServiceEventType.PRESENCE_UPDATED);
      subs.forEach((s) => s.listener(payload));
    });

    return hubConnection;
  };

  const connect = async () => {
    if (isConnected.value) return;

    connection = await createConnection();
    isIntentionalDisconnect = false;

    try {
      await connection.start();
      isConnected.value = true;
      reconnectAttempts = 0;
    } catch (error: any) {
      console.error("realtime.store -> initial connect failed", error);

      if (error?.message?.includes("401")) {
        console.warn("realtime.store -> token expired? skipping reconnect.");
        throw error;
      }

      scheduleReconnect();
      throw error;
    }
  };

  const disconnect = async (isClosing = false) => {
    if (!connection) return;

    isIntentionalDisconnect = true;

    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }

    await connection.stop();
    isConnected.value = false;

    if (isClosing) {
      connection = null;
    }
  };

  const enterView = async (viewId: string) => {
    if (!connection) return;
    try {
      await connection.invoke(ViewsHubFunction.ENTER_VIEW, viewId);
    } catch (error) {
      console.error("realtime.store -> enterView failed:", error);
    }
  };

  const leaveView = async (viewId: string) => {
    if (!connection) return;
    try {
      await connection.invoke(ViewsHubFunction.LEAVE_VIEW, viewId);
    } catch (error) {
      console.error("realtime.store -> leaveView failed:", error);
    }
  };

  window.addEventListener('online', () => isOffline.value = false);
  window.addEventListener('offline', () => isOffline.value = true);

  const isOffline = ref(false);

  watch(() => isOffline, async () => {
    if (isOffline.value) {
      await disconnect();
    } else {
      await connect();
    }
  });

  document.addEventListener("visibilitychange", async () => {
    if (!connection) return;

    if (document.hidden) {
      if (connection.state !== signalR.HubConnectionState.Connected) return;
      await disconnect();
    } else {
      await connect();
    }
  });

  return {
    connect,
    disconnect,
    isConnected,
    subscribe,
    unsubscribe,
    enterView,
    leaveView,
  };
});
